import { Offcanvas, Row } from 'react-bootstrap';
import React from 'react';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { FormProvider, useForm } from 'react-hook-form';
import { AddEntityDrawerProps } from './EntityListProps';
import { useCreateStrikeTeam } from './hooks/strikeTeamHooks';
import DrawerWrapper, { DrawerFooter } from './DrawerWrapper';
import FormTextField from './formFields/FormTextField';
import FormTextAreaField from './formFields/FormTextAreaField';
import FormSelectField from './formFields/FormSelectField';
import { useActualIncidentId } from './hooks/commonHooks';
import { STRIKE_TEAM_STATUSES_OBJECT } from 'components/IAP/ICSConstants';
import { selectGroupGuid, useAppSelector } from '../../slices/commonSelectors';

type FormFields = {
  id: string;
  strike_team_id: string;
  name: string;
  description?: string;
  group_guid: string;
  work_assignment_id?: string;
  resource_sets?: string[];
  status: string;
  archived?: boolean;
  leader: string;
  leader_contact: string;
  special_instructions: string;
};

const StrikeTeamAddDrawer: React.FC<AddEntityDrawerProps> = ({ toggle }) => {
  const form = useForm<FormFields>({ mode: 'onChange' });
  const { handleSubmit, watch } = form;

  const watchedName = watch('name');
  const watchedStatus = watch('status');

  const mutation = useCreateStrikeTeam();

  const incidentId = useActualIncidentId();
  const group_guid = useAppSelector(selectGroupGuid);

  const onNewStrikeTeamDataSubmit = async (formData: FormFields) => {
    const payload = {
      ...formData,
      archived: formData.archived ?? false,
      incident_id: incidentId,
      group_guid: group_guid,
    };
    await mutation.mutateAsync(payload);
    toggle();
  };

  return (
    <DrawerWrapper toggle={toggle} title="Add Strike Team">
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onNewStrikeTeamDataSubmit)}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <Offcanvas.Body>
            <Row>
              <FormTextField
                title="Name"
                fieldName="name"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 255,
                    message: 'The maximum length is 255 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormTextAreaField
                title="Description"
                fieldName="description"
                rules={{
                  maxLength: {
                    value: 300,
                    message: 'The maximum length is 300 characters',
                  },
                }}
                variant="full"
              />
            </Row>
            <Row>
              <FormSelectField
                title="Status"
                fieldName="status"
                rules={{
                  required: { value: true, message: 'This field is required' },
                }}
                options={STRIKE_TEAM_STATUSES_OBJECT}
                variant="half"
              />
            </Row>
            <Row>
              <FormTextField
                title="Leader"
                fieldName="leader"
                rules={{
                  maxLength: {
                    value: 255,
                    message: 'This maximum length is 255 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormTextField
                title="Leader Contact #"
                fieldName="leader_contact"
                rules={{
                  maxLength: {
                    value: 255,
                    message: 'This maximum length is 255 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormTextAreaField
                title="Special Instructions"
                fieldName="special_instructions"
                variant="full"
              />
            </Row>
            {/* Add other fields as necessary */}
          </Offcanvas.Body>
          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={toggle}
              >
                Close
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={!watchedName || !watchedStatus || mutation.isPending}
              >
                Save
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerWrapper>
  );
};

export default StrikeTeamAddDrawer;
